// components/Experience.js
import { Button, Label, Modal, TextInput, Datepicker, Select, Textarea, Checkbox, Carousel } from 'flowbite-react';
import { useState } from 'react';
import useExperienceController from '../controllers/useExperienceController';
import { useSession } from '../context/SessionContext';

function Experience() {
  const { session } = useSession();
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentExperience, setCurrentExperience] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isCurrent, setIsCurrent] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    city: '',
    country: '',
    position: '',
    start: '',
    end: '',
    type: '',
    description: '',
    achievements: '',
    certificate: '',
  });
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState('');

  const {
    experiences,
    loading,
    error,
    addExperience,
    updateExperience,
    deleteExperience,
    deleteImage,
  } = useExperienceController(session);

  const onCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentExperience(null);
    setImages([]);
    setImageError('');
    setIsCurrent(false);
    setFormData({
      company: '',
      city: '',
      country: '',
      position: '',
      start: '',
      end: '',
      type: '',
      description: '',
      achievements: '',
      certificate: '',
    });
    setStartDate(new Date());
    setEndDate(new Date());
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const validFiles = [];
      for (const file of files) {
        if (file.size > 500 * 1024) {
          setImageError('Each image must be less than 500KB');
          return;
        }
        validFiles.push(file);
      }
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setImageError('');
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (image, index) => {
    try {
      if (isEditMode && image.path) {
        await deleteImage(image.path);
        setCurrentExperience(prev => ({
          ...prev,
          images: prev.images.filter(img => img.path !== image.path)
        }));
      }
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.company ||
      !formData.city ||
      !formData.country ||
      !formData.position ||
      !formData.type ||
      formData.type === 'Choose Employment Type'
    ) {
      alert('Please fill in all required fields: Company, City, Country, Position, and Employment Type.');
      return;
    }

    if (images.length > 0 && imageError) {
      alert('Please fix image errors before submitting');
      return;
    }

    try {
      const experienceData = {
        ...formData,
        start: startDate.toISOString(),
        end: isCurrent ? null : endDate.toISOString(),
        description: formData.description || null,
        achievements: formData.achievements || null,
      };

      if (isEditMode && currentExperience) {
        const newImageFiles = images.filter(img => img instanceof File);
        await updateExperience(currentExperience.id, experienceData, newImageFiles);
      } else {
        await addExperience(experienceData, images);
      }

      onCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleEdit = (experience) => {
    setCurrentExperience(experience);
    setIsEditMode(true);
    setFormData({
      company: experience.company,
      city: experience.city,
      country: experience.country,
      position: experience.position,
      start: experience.start,
      end: experience.end,
      type: experience.type,
      description: experience.description || '',
      achievements: experience.achievements || '',
      certificate: experience.certificate || '',
    });
    setStartDate(new Date(experience.start));
    setEndDate(experience.end ? new Date(experience.end) : new Date());
    setIsCurrent(!experience.end);
    setImages(experience.images || []);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this experience?')) {
      await deleteExperience(id);
    }
  };

  // Sort experiences by start date (oldest to newest)
  const sortedExperiences = [...experiences].sort((a, b) => 
    new Date(a.start) - new Date(b.start)
  );

  return (
    <div className="flex">
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className="my-6 md:col-span-2 md:pl-6">
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl 2xl:text-8xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Experience
              </span>
            </h1>
            <p className="max-w-3xl md:mt-6 mt-2 mr-4 md:text-lg text-sm text-left text-gray-700 dark:text-white">
              Add your experiences to highlight your skills and achievements in jobs, organizations, or projects.
            </p>
          </div>
          <div className="mx-auto my-auto md:col-span-1">
            <Button color="blue" onClick={() => setOpenModal(true)}>
              <b>+</b> Add Your Experience
            </Button>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
              Error: {error}
            </div>
          ) : (
            <div className="relative md:pl-8 md:ml-4 pl-2 ml-2">
              {/* Vertical line */}
              <div 
                className="absolute left-0 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                style={{ marginLeft: '7px' }}
              ></div>
              
              {sortedExperiences.map((exp, index) => (
                <div 
                  key={exp.id} 
                  className="relative mb-8 md:pl-6 pl-4 group"
                >
                  {/* Timeline dot */}
                  <div 
                    className="absolute w-4 h-4 bg-blue-600 rounded-full -left-2 top-5 border-4 border-white dark:border-gray-900"
                    style={{ left: '4px' }}
                  ></div>
                  
                  {/* Experience card */}
                  <div className="bg-white rounded-lg shadow-lg dark:bg-gray-800 md:p-6 p-3 transition-all duration-300 group-hover:shadow-xl group-hover:translate-x-2">
                    <div className="flex flex-col md:flex-row">
                      {exp.images && exp.images.length > 0 && (
                        <div className="md:w-2/5 lg:w-1/3 h-64 md:h-auto mb-4 md:mb-0">
                          <Carousel>
                            {exp.images.map((image, idx) => (
                              <img
                                key={idx}
                                src={image.publicUrl}
                                alt={`Experience ${idx + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ))}
                          </Carousel>
                        </div>
                      )}
                      
                      <div className={`flex-1 ${exp.images ? 'md:ml-6' : ''}`}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <h2 className="md:text-xl font-bold text-gray-800 dark:text-white">
                            {exp.position} @ {exp.company}
                          </h2>
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1 md:mt-0">
                            {new Date(exp.start).toLocaleDateString('en-GB', {
                              month: 'short',
                              year: 'numeric'
                            })} - {exp.end ? 
                              new Date(exp.end).toLocaleDateString('en-GB', {
                                month: 'short',
                                year: 'numeric'
                              }) : 'Present'}
                          </span>
                        </div>
                        
                        <div className="flex items-center mb-4 text-gray-600 dark:text-gray-300">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ minWidth: '20px' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span>{exp.city}, {exp.country}</span>
                        </div>

                        {exp.description && (
                          <p className="mb-4 text-gray-700 dark:text-gray-300">
                            {exp.description}
                          </p>
                        )}

                        {exp.achievements && (
                          <div className="bg-gray-50 dark:bg-gray-700 md:p-4 p-2 rounded-lg mb-4">
                            <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-white">
                              Key Achievements:
                            </h3>
                            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                              {exp.achievements.split(";").map((item, idx) => (
                                <li key={idx}>{item.trim()}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button 
                            color="red" 
                            onClick={(e) => { e.stopPropagation(); handleDelete(exp.id); }}
                            size="sm"
                            className="flex-1 md:flex-none"
                          >
                            Delete
                          </Button>
                          <Button 
                            color="blue" 
                            onClick={(e) => { e.stopPropagation(); handleEdit(exp); }}
                            size="sm"
                            className="flex-1 md:flex-none"
                          >
                            Edit
                          </Button>
                          {exp.certificate && (
                            <a
                              href={exp.certificate}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800 flex-1 md:flex-none justify-center"
                            >
                              View Certificate
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal for Adding/Editing Experience */}
        <Modal show={openModal} onClose={onCloseModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Work Experience' : 'Add Your Work Experience'}
              </h3>
              <div>
                <Label htmlFor="company" value="Company Name *" />
                <TextInput id="company" placeholder="e.g. Google" required value={formData.company} onChange={handleInputChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city" value="City *" />
                  <TextInput id="city" placeholder="e.g. San Francisco" required value={formData.city} onChange={handleInputChange} />
                </div>
                <div>
                  <Label htmlFor="country" value="Country *" />
                  <TextInput id="country" placeholder="e.g. USA" required value={formData.country} onChange={handleInputChange} />
                </div>
              </div>
              <div>
                <Label htmlFor="position" value="Position *" />
                <TextInput id="position" placeholder="e.g. Senior Developer" required value={formData.position} onChange={handleInputChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start" value="Start Date *" />
                  <Datepicker id="start" selected={startDate} onChange={(date) => setStartDate(date)} required />
                </div>
                <div>
                  <Label htmlFor="end" value="End Date" />
                  <Datepicker
                    id="end"
                    selected={isCurrent ? null : endDate}
                    onChange={(date) => setEndDate(date)}
                    disabled={isCurrent}
                  />
                  <div className="mt-2">
                    <Checkbox
                      id="isCurrent"
                      checked={isCurrent}
                      onChange={(e) => setIsCurrent(e.target.checked)}
                    />
                    <Label htmlFor="isCurrent" className="ml-2">
                      I currently work here
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="type" value="Employment Type *" />
                <Select id="type" required value={formData.type} onChange={handleInputChange}>
                  <option>Choose Employment Type</option>
                  <option>Full-time</option>
                  <option>Part-time</option>
                  <option>Contract</option>
                  <option>Internship</option>
                  <option>Volunteer</option>
                  <option>Personal Project</option>
                  <option>Bachelor Final Project</option>
                  <option>Master Thesis Project</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="description" value="Description" />
                <Textarea id="description" placeholder="Describe your role and responsibilities" value={formData.description} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="achievements" value="Key Achievements (separate with ; for each Key Achievements)" />
                <Textarea id="achievements" placeholder="List your key achievements" value={formData.achievements} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="certificate" value="Certificate Link" />
                <TextInput id="certificate" placeholder="e.g. https://drive.google ..." value={formData.certificate} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="images" value="Image Documentation (Max 500KB per image)" />
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                />
                {imageError && <p className="mt-2 text-sm text-red-600 dark:text-red-500">{imageError}</p>}
                <div className="mt-4 space-y-2">
                  {images?.map((image, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        {image.publicUrl && (
                          <img src={image.publicUrl} alt={`Preview ${index + 1}`} className="w-12 h-12 object-cover rounded mr-3" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                          {image.path ? image.path.split('/').pop() : image.name}
                        </span>
                      </div>
                      <Button 
                        color="red" 
                        size="xs" 
                        onClick={() => handleRemoveImage(image, index)}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full">
                <Button color="blue" type="submit" className="w-full">
                  {isEditMode ? 'Update Work Experience' : 'Add Work Experience'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Experience;